import React from 'react';
import { Box, Alert } from '@mui/material';
import { useChatbotState } from './hooks/useChatbotState';
import { CopilotHeader } from './components/CopilotHeader';
import { CopilotWindow } from './components/CopilotWindow';
import { CopilotLauncher } from './components/CopilotLauncher';
import { ChatMessages } from './components/ChatMessages';
import { ChatInput } from './components/ChatInput';

interface ShipmentChatBotProps {
  orgCode?: string;
  botEnabled?: boolean;
  onNavigate?: (url: string) => void;
  apiBaseUrl?: string;
}

// ==================== MAIN ORCHESTRATOR ====================
const SpeedboxChatBot: React.FC<ShipmentChatBotProps> = ({
  orgCode = 'DEFAULT-ORG',
  botEnabled = true,
  onNavigate,
  apiBaseUrl,
}) => {
  const state = useChatbotState({ orgCode, onNavigate, apiBaseUrl });

  if (!botEnabled) {
    return null;
  }

  const hasMessages = state.messages.length > 0;

  const handleSuggestionClick = (query: string, intent?: string | null) => {
    state.setInputValue(query);
    setTimeout(() => state.handleSendMessage(query, intent || undefined), 100);
  };

  return (
    <>
      {/* INJECT ANIMATION KEYFRAMES */}
      <style>{`
        @keyframes geminiWave {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes geminiPulse {
          0% {
            box-shadow: 0 0 0 0 rgba(66, 133, 244, 0.4), 0 0 0 0 rgba(155, 114, 203, 0.3);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(217, 101, 112, 0), 0 0 0 20px rgba(244, 180, 0, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(66, 133, 244, 0), 0 0 0 0 rgba(155, 114, 203, 0);
          }
        }
        @keyframes geminiBorderGlowLoop {
          0% {
            border-color: #4285F4;
            box-shadow: 0 0 8px rgba(66, 133, 244, 0.6), -4px 0px 16px rgba(0, 0, 0, 0.15);
          }
          25% {
            border-color: #9B72CB;
            box-shadow: 0 0 8px rgba(155, 114, 203, 0.6), -4px 0px 16px rgba(0, 0, 0, 0.15);
          }
          50% {
            border-color: #D96570;
            box-shadow: 0 0 8px rgba(217, 101, 112, 0.6), -4px 0px 16px rgba(0, 0, 0, 0.15);
          }
          75% {
            border-color: #F4B400;
            box-shadow: 0 0 8px rgba(244, 180, 0, 0.6), -4px 0px 16px rgba(0, 0, 0, 0.15);
          }
          100% {
            border-color: #4285F4;
            box-shadow: 0 0 8px rgba(66, 133, 244, 0.6), -4px 0px 16px rgba(0, 0, 0, 0.15);
          }
        }
      `}</style>

      {/* COPILOT LAUNCHER */}
      <CopilotLauncher
        isOpen={state.isOpen}
        onClick={() => state.setIsOpen(!state.isOpen)}
        hasMessages={hasMessages}
        messageCount={state.messages.length}
      />

      {/* COPILOT SIDE-PANEL WINDOW */}
      <CopilotWindow isOpen={state.isOpen} chatWindowRef={state.chatWindowRef} isActivating={state.isActivating}>
        {/* HEADER */}
        <CopilotHeader
          isActivating={state.isActivating}
          setIsOpen={state.setIsOpen}
          onClearChat={state.triggerClearChatConfirm}
          hasMessages={hasMessages}
        />

        {/* GEMINI ACTIVATION BAR */}
        <Box
          sx={{
            height: '3px',
            width: '100%',
            opacity: state.isActivating ? 1 : 0,
            transition: 'opacity 0.4s ease',
            background: 'linear-gradient(90deg, #4285F4, #9B72CB, #D96570, #F4B400, #4285F4)',
            backgroundSize: '200% 100%',
            animation: state.isActivating ? 'geminiWave 1.5s linear infinite' : 'none',
            flexShrink: 0,
          }}
        />

        {/* ERROR ALERT */}
        {state.apiError && (
          <Alert severity="error" onClose={() => state.setApiError(null)} sx={{ margin: 1.5, borderRadius: 2 }}>
            {state.apiError}
          </Alert>
        )}

        {/* MESSAGES AREA */}
        <ChatMessages
          messages={state.messages}
          isLoading={state.isLoading}
          isActivating={state.isActivating}
          handleQuickAction={state.handleQuickAction}
          handleBotStateAction={state.handleBotStateAction}
          handleRelatedChipClick={state.handleRelatedChipClick}
          messagesEndRef={state.messagesEndRef}
          onEndConversation={state.triggerEndConversationConfirm}
          dismissConfirmation={state.dismissConfirmation}
          handleEndConversation={state.handleEndConversation}
          handleClearChat={state.handleClearChat}
          handleMissingFieldClick={state.handleMissingFieldClick}
          handleIntentConfirmation={state.handleIntentConfirmation}
          isAwaitingIntentConfirmation={state.currentBotState?.toLowerCase() === 'awaiting_intent_confirmation'}
          chatbotExtractionLoading={state.chatbotExtractionLoading}
          activeConfirmation={state.activeConfirmation}
          confirmationLoading={state.confirmationLoading}
          handleConfirmationAction={state.handleConfirmationAction}
        />

        {/* INPUT BAR AREA */}
        <ChatInput
          activeSuggestions={hasMessages ? state.activeSuggestions : []}
          attachedFiles={state.attachedFiles}
          inputValue={state.inputValue}
          setInputValue={state.setInputValue}
          isLoading={state.isLoading}
          handleSuggestionClick={handleSuggestionClick}
          handleSendMessage={state.handleSendMessage}
          handleFileSelect={state.handleFileSelect}
          removeFile={state.removeFile}
          fileInputRef={state.fileInputRef}
          inputRef={state.inputRef}
          activeIntent={state.activeIntent}
          missingFields={state.missingFields}
          isAwaitingIntentConfirmation={
            state.currentBotState?.toLowerCase() === 'awaiting_intent_confirmation' || !!state.activeConfirmation
          }
          chatbotExtractionLoading={state.chatbotExtractionLoading}
        />
      </CopilotWindow>
    </>
  );
};

export default SpeedboxChatBot;
